import { EXAM_EVENT_DATA_TEST_ID } from '@e2e/constants/exam-center';
import {
  AddRoundedIcon,
  Button,
  Flex,
  FormTimeInput,
  Grid,
  styled,
  Text,
  Tooltip,
  WatchLaterOutlinedIcon,
} from '@shared/components';
import { formatTimeToThaiUnits } from '@shared/utils';

import { MAX_SESSIONS } from '../../constants';
import { type Session } from '../../hooks';
import { type DisableAddButtonReason } from '../../types';
import { SessionInputCard } from '../session-input-card';

const StyledTextForTo = styled(Text)(({ theme }) => ({
  padding: theme.spacing(8.5, 1, 2.5, 1),
}));

const SummaryWrapper = styled(Flex)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(2),
  color: theme.palette.text.primary,
  padding: theme.spacing(3, 4),
  borderRadius: theme.shape.rounded(2),
  backgroundColor: theme.palette.grey[50],
  border: '1px solid',
  borderColor: theme.palette.grey[300],
}));

const DisableAddButtonReasonMessages: Record<DisableAddButtonReason, string> = {
  examDateNotFilled: 'กรุณากรอกเวลาสอบทั้งหมดก่อน',
  sessionLimitReached: `ไม่สามารถเพิ่มช่วงสอบได้อีก (สูงสุด ${MAX_SESSIONS} ช่วง)`,
};

export interface ExamTimeFormFieldsProps {
  slotProps: {
    sessions: {
      data: Session[];
      totalDuration: {
        hours: number;
        minutes: number;
      };
      onAddSession: () => void;
      onRemoveSession: (order: number) => void;
      errorMessage?: string;
    };
    disableAddButton: {
      shouldDisable?: boolean;
      reason?: DisableAddButtonReason;
      hidden?: boolean;
    };
  };
}

export default function ExamTimeFormFields(props: Readonly<ExamTimeFormFieldsProps>) {
  const { slotProps: { sessions, disableAddButton } = {} } = props;
  const {
    data: sessionData = [],
    totalDuration = {
      hours: 0,
      minutes: 0,
    },
    onAddSession,
    onRemoveSession,
    errorMessage,
  } = sessions ?? {};
  const { shouldDisable = false, reason: disableReason, hidden = false } = disableAddButton ?? {};

  const shouldDisplayTotalDuration = sessionData.length > 0;

  return (
    <Flex sx={{ gap: (theme) => theme.spacing(4), flexDirection: 'column' }}>
      <Grid
        sx={{
          columnGap: (theme) => theme.spacing(2),
          rowGap: (theme) => theme.spacing(2),
          gridTemplateColumns: 'minmax(max-content, 200px) max-content minmax(max-content, 200px)',
        }}
      >
        <FormTimeInput
          name='examDate.startTime'
          label='เวลาเริ่มต้น'
          required
          data-testid={EXAM_EVENT_DATA_TEST_ID.EXAM_START_TIME_INPUT}
        />
        <StyledTextForTo>ถึง</StyledTextForTo>
        <FormTimeInput
          name='examDate.endTime'
          label='เวลาสิ้นสุด'
          required
          data-testid={EXAM_EVENT_DATA_TEST_ID.EXAM_END_TIME_INPUT}
        />
      </Grid>
      {sessionData.map((session) => (
        <SessionInputCard
          key={session.key}
          order={session.order}
          duration={session.duration}
          inputName={{
            start: session.name.start,
            end: session.name.end,
          }}
          errorMessage={session.errorMessage}
          onClose={() => onRemoveSession?.(session.order)}
        />
      ))}
      {shouldDisplayTotalDuration ? (
        <SummaryWrapper>
          <WatchLaterOutlinedIcon />
          <Text variant='body1Semibold'>
            รวมระยะเวลาสอบ: {formatTimeToThaiUnits(totalDuration)}
          </Text>
        </SummaryWrapper>
      ) : null}
      {errorMessage ? (
        <Text
          variant='body2'
          sx={(theme) => ({
            color: theme.palette.error.main,
          })}
        >
          {errorMessage}
        </Text>
      ) : null}
      <Flex
        sx={(theme) => ({
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: theme.spacing(4),
          display: hidden ? 'none' : 'flex',
        })}
      >
        <Tooltip
          placement='left'
          arrow
          title={disableReason ? DisableAddButtonReasonMessages[disableReason] : ''}
        >
          <Button
            variant='outlined'
            color='secondary'
            size='large'
            startIcon={<AddRoundedIcon />}
            onClick={onAddSession}
            disabled={shouldDisable}
            sx={{
              '&.Mui-disabled': {
                pointerEvents: 'auto',
              },
            }}
          >
            เพิ่มช่วงสอบ
          </Button>
        </Tooltip>
        <Text variant='body2'>(เหมาะสำหรับการสอบที่มีหลายช่วงในวันเดียวกัน เช่น รอบเช้า-บ่าย)</Text>
      </Flex>
    </Flex>
  );
}
